import { clsx } from 'clsx';
import { Link, useNotify, useRedirect, useRefresh } from 'react-admin';
import React, { JSX, useEffect, useMemo, useState } from 'react';

import { Credit, CreditStatus, Doctype } from '@types';
import { DoctypeDocument } from '@pages/credit/DoctypeDocument';
import { BlueButton, InitialPage, LightTooltip } from '@components';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import {
  doctypeHttp,
  contractHttp,
  creditHttp,
  ContractsVerification,
  DocumentsCheckResults,
} from '@network';
import cls from './credit-contracts-details.module.css';
import { rbac } from '@utils';

interface CreditContractsDetailsProps {
  credit: Credit;
  setCredit: any;
  isSuperadmin: boolean;
}

const Icon = ({ value }: {value: boolean}) => value
  ? <CheckCircleFilled className={cls.verificationIconValid} />
  : <CloseCircleFilled className={cls.verificationIconInvalid} />;

export const CreditContractsDetails = ({
  credit,
  setCredit,
  isSuperadmin,
}: CreditContractsDetailsProps) => {
  const refresh = useRefresh();
  const redirect = useRedirect();
  const notify = useNotify();
  const [doctypes, setDoctypes] = useState<Doctype[]>([]);
  const [verification, setVerification] = useState<ContractsVerification | null>(null);

  const backTo = `/credit/${credit.id}?initial`;
  const creditDocsLink = `/credit/${credit.id}?scroll=docs`;
  const creditRiskLink = credit.risk ? `/credit-risk/${credit.risk.id}` : '';

  const handleGenContract = async (): Promise<void> => {
    try {
      const contract = await contractHttp.create({ creditId: credit.id });
      refresh();
      if (verification?.hasContract) {
        redirect(`/contract/${contract.id}`);
      } else {
        notify('Contract is generated');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const retentionLetterDoctype = useMemo(() => {
    return doctypes.find(doctype => doctype.isRetentionLetter);
  }, [doctypes]);

  const amortizationTableDoctype = useMemo(() => {
    return doctypes.find(doctype => doctype.isAmortizationTable);
  }, [doctypes]);

  const creditContractDoctype = useMemo(() => {
    return doctypes.find(doctype => doctype.isCreditContract);
  }, [doctypes]);

  const getValidateText = (
    obj: DocumentsCheckResults | undefined, field?: boolean,
  ): JSX.Element => {
    if (!obj) return <></>;

    const data: {title: string, total: number, completed: number}[] = [];
    for (const i in obj) {
      data.push({ title: i, total: obj[i].total, completed: obj[i].completed });
    }

    return field ? (
      <span className={cls.verificationCnt}>&nbsp;
        ({data.reduce((acc, i) => (acc + i.completed), 0)}
        /{data.reduce((acc, i) => (acc + i.total), 0)})
      </span>
    ) : (
      <>
        {data.map((item, i) => (
          <div key={i}>
            {item.title}
            <span className={cls.verificationCnt}>:&nbsp;({item.completed}/{item.total})</span>
          </div>
        ))}
      </>
    );
  };

  useEffect(() => {
    credit && (async () => {
      const res = await doctypeHttp.getAll();
      const verificationRes = await creditHttp.contractsVerification(credit.id);

      if (res) {
        setDoctypes(res);
      }

      setVerification(verificationRes || null);
    })();
  }, [credit]);

  // Determine if user has Credit or KYC area access
  const isCreditOrKyc = rbac.hasAreaCredit() || rbac.hasAreaKyc();
  let genDisabled = !verification
    || !verification.isContractValid
    || !isCreditOrKyc
    || !credit.requestContract
    || credit.status !== CreditStatus.IN_NEGOTIATIONS;

  if (verification?.hasContract) {
    genDisabled = false;
  }

  let genTitle = '';
  if (genDisabled && !genTitle && !verification?.isContractValid) {
    genTitle = 'Please, complete all required Verification to generate contract';
  }
  if (genDisabled && !genTitle && !isCreditOrKyc) {
    genTitle = 'Credit/KYC Team is required to generate contract';
  }
  if (genDisabled && !genTitle && !credit.requestContract) {
    genTitle = 'Request Contract was not sent by Sales Team';
  }
  if (genDisabled && !genTitle && credit.status !== CreditStatus.IN_NEGOTIATIONS) {
    genTitle = 'Credit status must be \'In Negotiations\' to generate contract';
  }

  return (
    <InitialPage title="Contracts" backTo={backTo} backText="Back to credit">
      <div className={cls.wrap}>
        {retentionLetterDoctype && (
          <div className={cls.item}>
            <div className={cls.content}>
              <DoctypeDocument
                doctype={retentionLetterDoctype}
                credit={credit}
                setCredit={setCredit}
                isSuperadmin={isSuperadmin}
                editable={true}
                downloadable={rbac.hasDownloadRestrictionRole()}
                uploadable={rbac.hasUploadPermissionRole()}
              />
            </div>
          </div>
        )}

        {amortizationTableDoctype && (
          <div className={cls.item}>
            <div className={cls.content}>
              <DoctypeDocument
                doctype={amortizationTableDoctype}
                credit={credit}
                setCredit={setCredit}
                isSuperadmin={isSuperadmin}
                editable={true}
                downloadable={rbac.hasDownloadRestrictionRole()}
                uploadable={rbac.hasUploadPermissionRole()}
              />
            </div>
          </div>
        )}

        {creditContractDoctype && (
          <div className={clsx(cls.item, cls.fullWidth)}>
            <div className={clsx(cls.content, cls.row)}>
              <div className={cls.contract}>
                <DoctypeDocument
                  doctype={creditContractDoctype}
                  credit={credit}
                  setCredit={setCredit}
                  isSuperadmin={isSuperadmin}
                  editable={true}
                  creditContractDoctype={creditContractDoctype}
                  downloadable={rbac.hasDownloadRestrictionRole()}
                  uploadable={rbac.hasUploadPermissionRole()}
                />
              </div>
              <div className={cls.integration}>
                <div className={cls.section}>
                  <div className={cls.section_content}>
                    <div className={cls.verification}>
                      <div className={cls.verificationTitle}>Verification</div>
                      <div className={cls.verificationList}>
                        <div className={cls.verificationItem}>
                          <div className={cls.verificationIcon}>
                            <Icon value={verification?.creditDocs.isValid || false} />
                          </div>
                          <div className={cls.verificationInfo}>
                            <LightTooltip
                              placement="top"
                              title={getValidateText(verification?.creditDocs?.values)}
                              className={cls.tooltip}
                            >
                              <div>
                                <Link to={creditDocsLink}>Full Docs - Revised</Link>
                                {getValidateText(verification?.creditDocs?.values, true)}
                              </div>
                            </LightTooltip>
                          </div>
                        </div>
                        <div className={cls.verificationItem}>
                          <div className={cls.verificationIcon}>
                            <Icon value={verification?.retentionLetter.isValid || false} />
                          </div>
                          <div className={cls.verificationInfo}>Retention Letter</div>
                        </div>
                        <div className={cls.verificationItem}>
                          <div className={cls.verificationIcon}>
                            <Icon value={verification?.risk.isValid || false} />
                          </div>
                          <div className={cls.verificationInfo}>
                            {creditRiskLink ? (
                              <Link to={creditRiskLink}>Risk Analysis - Completed</Link>
                            ) : 'Risk Analysis'}
                          </div>
                        </div>
                        <div className={cls.verificationItem}>
                          <div className={cls.verificationIcon}>
                            <Icon value={verification?.terms.isValid || false} />
                          </div>
                          <div className={cls.verificationInfo}>
                            {creditRiskLink ? (
                              <Link to={creditRiskLink}>Credit Terms - Completed</Link>
                            ) : 'Credit Terms'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={cls.actions}>
                      {genTitle ? (
                        <LightTooltip extraWidth title={genTitle}>
                          <div>
                            <BlueButton
                              onClick={handleGenContract}
                              className={cls.genBtn}
                              disabled={genDisabled}
                              title={genTitle || undefined}
                            >
                              {verification?.hasContract ? 'Open Contract' : 'Generate Contract'}
                            </BlueButton>
                          </div>
                        </LightTooltip>
                      ) : (
                        <BlueButton
                          onClick={handleGenContract}
                          className={cls.genBtn}
                          disabled={genDisabled}
                        >
                          {verification?.hasContract ? 'Open Contract' : 'Generate Contract'}
                        </BlueButton>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </InitialPage>
  );
};
